import React from "react";
import axios from "axios";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import { Link as RouterLink } from "react-router-dom";
import { styled } from "@mui/material/styles";
import {
  Card,
  Link,
  Container,
  Typography,
  TextField,
  Button,
} from "@mui/material";
import Page from "../components/Page";
import Logo from "../components/Logo";
import useResponsive from "../hooks/useResponsive";
import { useTheme } from "@emotion/react";

// ---------- VALIDATION SCHEMA ----------
const schema = yup.object().shape({
  email: yup
    .string()
    .required("Email is required.")
    .email("Invalid email format.")
    .max(40, "Email must be less than 40 characters."),
});

// ---------- STYLES ----------
const RootStyle = styled("div")(({ theme }) => ({
  [theme.breakpoints.up("md")]: { display: "flex" },
}));

const HeaderStyle = styled("header")(({ theme }) => ({
  top: 0,
  zIndex: 9,
  lineHeight: 0,
  width: "100%",
  display: "flex",
  alignItems: "center",
  position: "absolute",
  padding: theme.spacing(3),
  justifyContent: "space-between",
  [theme.breakpoints.up("md")]: {
    alignItems: "flex-start",
    padding: theme.spacing(7, 5, 0, 7),
  },
}));

const SectionStyle = styled(Card)(({ theme }) => ({
  width: "100%",
  maxWidth: 464,
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  margin: theme.spacing(2),
}));

const ContentStyle = styled("div")(({ theme }) => ({
  maxWidth: 480,
  margin: "auto",
  minHeight: "100vh",
  display: "flex",
  flexDirection: "column",
  padding: theme.spacing(12, 0),
}));

// ---------- COMPONENT ----------
export default function ForgotPassword() {
  const theme = useTheme();
  const smUp = useResponsive("up", "sm");
  const mdUp = useResponsive("up", "md");

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm({ resolver: yupResolver(schema) });

  const [serverMsg, setServerMsg] = React.useState("");

  const onSubmit = async (data) => {
    setServerMsg("");

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_LIVE_URL}/adminPanel/forgotPassword`,
        data
      );

      setServerMsg(response.data.msg);
      reset();
    } catch (err) {
      setServerMsg(err.response?.data?.msg || "Server error.");
    }
  };

  return (
    <Page title="Forgot Password">
      <RootStyle>
        {/* HEADER SAME AS LOGIN PAGE */}
        <HeaderStyle>
          <Logo sx={{ width: "150px", height: "80px", marginLeft: "65%" }} />

          {smUp && (
            <Typography variant="body2" sx={{ mt: { md: -2 } }}>
              Remember your password?{" "}
              <Link variant="subtitle2" component={RouterLink} to="/login">
                Login
              </Link>
            </Typography>
          )}
        </HeaderStyle>

        {/* LEFT SECTION IMAGE */}
        {mdUp && (
          <SectionStyle>
            <Typography variant="h3" sx={{ px: 5, mt: 18, mb: 5 }}>
              Reset Your Password
            </Typography>
            <img src="/static/illustrations/illustration_login.png" alt="forgot" />
          </SectionStyle>
        )}

        {/* CONTENT SECTION */}
        <Container maxWidth="sm">
          <ContentStyle>
            <Typography variant="h4" gutterBottom>
              Forgot your{" "}
              <span style={{ color: theme.palette.primary.mainTheme }}>Password?</span>
            </Typography>

            <Typography sx={{ color: "text.secondary", mb: 5 }}>
              Enter your registered email address and we’ll send you a reset link.
            </Typography>

            <form onSubmit={handleSubmit(onSubmit)}>
              <Typography sx={{ mb: 1, fontWeight: 500 }}>Email Address</Typography>

              <TextField
                fullWidth
                placeholder="example@domain.com"
                {...register("email")}
                error={!!errors.email}
                helperText={errors.email?.message}
                sx={{ mb: 3 }}
              />

              <Button
                fullWidth
                type="submit"
                variant="contained"
                disabled={isSubmitting}
                sx={{
                  py: 1.5,
                  background: "linear-gradient(90deg, #9a2eff, #8b24f4)",
                }}
              >
                {isSubmitting ? "Sending..." : "Send Reset Link"}
              </Button>
            </form>

            {serverMsg && (
              <Typography sx={{ mt: 3, fontWeight: 500 }}>{serverMsg}</Typography>
            )}

            {!smUp && (
              <Typography variant="body2" align="center" sx={{ mt: 3 }}>
                Remember your password?{" "}
                <Link variant="subtitle2" component={RouterLink} to="/login">
                  Login
                </Link>
              </Typography>
            )}
          </ContentStyle>
        </Container>
      </RootStyle>
    </Page>
  );
}
